import { Heading, Theme, useIsTheme } from '@afterpaytouch/core'
import { unwrapResult } from '@reduxjs/toolkit'
import { useTranslation } from '../../utils/convergence'
import React, { useEffect } from 'react'
import { ErrorCode } from '@afterpaytouch/portal-api/types'
import { CreatePassword, Head, ModuleContainer } from '../../../src/component'
import { Route, usePostLoginRoute } from '../../../src/config/router'
import { TrackPageViewed } from '../../../src/integrations/monitoring'
import { CheckoutPage, TerminalErrorCode } from '../../../src/model'
import { accountLookup, setCheckoutError, useAppDispatch, useAppRouter, useConsumerSelector } from '../../../src/state'
import { markUsFebConsentInteractiveLogin } from '../../flow/one-offer-page/consentLoginSignal'
import { useCheckoutSizes } from '../../../src/utils/responsive'

export const CreatePasswordPage: CheckoutPage = () => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const dispatch = useAppDispatch()
  const { headingSize } = useCheckoutSizes()
  const { hasPassword } = useConsumerSelector()
  const postLoginRoute = usePostLoginRoute()
  const isAPTheme = useIsTheme(Theme.AP)

  const context = { ...(!hasPassword && { context: 'firstPassword' }) }

  const onSuccess = async (): Promise<void> => {
    const result = unwrapResult(await dispatch(accountLookup()))
    // First-password creation completes an interactive login but is deliberately not a
    // remember-me mint site — mark only the US FEB consent signal, with the same LOGGED_IN gate
    // and SIGN_UP-detour convergence as Password.tsx.
    if (postLoginRoute === Route.LOGGED_IN) {
      markUsFebConsentInteractiveLogin()
    }
    if (result.profileConfirmed) {
      router.push(postLoginRoute, result.preferredLocale)
    } else {
      router.push(Route.SIGN_UP)
    }
  }

  const onError = async (): Promise<void> => {
    dispatch(
      setCheckoutError({
        error: TerminalErrorCode.AccountFrozen,
        errorCode: ErrorCode.AccountFrozen,
      })
    )
    router.push(Route.TERMINAL_ERROR)
  }

  const pageTitle = `${t('common:brandName')}` // TODO: [accessibility] fix page title to be descriptive

  useEffect(() => {
    router.prefetch(Route.SIGN_UP)
    router.prefetch(Route.TERMINAL_ERROR)
    router.prefetch(Route.LOGGED_IN)
  }, [])

  return (
    <>
      <Head title={pageTitle} />
      <ModuleContainer padding={'None'} showBorder={false} withoutContainer={isAPTheme}>
        <Heading size={headingSize}>{t('createPassword:heading', context)}</Heading>
      </ModuleContainer>
      <CreatePassword onSuccess={onSuccess} onError={onError} />
      <TrackPageViewed />
    </>
  )
}
